import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Alert } from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { useUser } from '../../context/userContext';
import { getApplications, reviewApplication, approveApplication, rejectApplication } from '../../api/invitationApi';
import { getAllAgentGroups } from '../../api/userApi';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';
import handleAgentName from '../../utils/userUtils';
import AgreementContent from '../public/agreementContent';

const PP = "'Poppins', sans-serif";
const STATUS_COLORS = {
  PendingReview: { bg: '#FEF3C7', color: '#92400E', label: 'Pending Review' },
  PendingApproval: { bg: '#DBEAFE', color: '#1E40AF', label: 'Pending Final Approval' },
  Approved: { bg: '#D1FAE5', color: '#065F46', label: 'Approved' },
  Rejected: { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected' },
};

export default function AgentApplications({ embedded = false } = {}) {
  const { user } = useUser();
  const [applications, setApplications] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [reviewForm, setReviewForm] = useState({ groupId: '', personalComm: '', doc: '', toc: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [agreementDialog, setAgreementDialog] = useState(false);
  const [agreementApp, setAgreementApp] = useState(null);

  const openAgreement = (app) => {
    setAgreementApp(app);
    setAgreementDialog(true);
  };

  const fetchData = useCallback(async () => {
    try {
      const [appsRes, groupsRes] = await Promise.all([getApplications(), getAllAgentGroups()]);
      setApplications(appsRes.data);
      setGroups(groupsRes.data);
    } catch (err) {
      console.error('Error fetching applications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openReview = (app) => {
    setSelectedApp(app);
    setReviewForm({
      groupId: app.groupId ?? app.inviterGroupId ?? '',
      personalComm: app.personalComm ?? '',
      doc: app.downlineOverridingComm ?? '',
      toc: app.teamOverridingComm ?? '',
    });
    setReviewDialog(true);
  };

  const handleReview = async () => {
    try {
      await reviewApplication({
        applicationId: selectedApp.applicationId,
        groupId: parseInt(reviewForm.groupId),
        personalComm: parseFloat(reviewForm.personalComm) || 0,
        downlineOverridingComm: reviewForm.doc !== '' ? parseFloat(reviewForm.doc) : null,
        teamOverridingComm: reviewForm.toc !== '' ? parseFloat(reviewForm.toc) : null,
        reviewedBy: user.username,
      });
      setReviewDialog(false);
      fetchData();
    } catch (err) {
      alert('Failed: ' + (err.response?.data || err.message));
    }
  };

  const handleApprove = async (appId) => {
    if (!window.confirm('Approve this application? This will activate the agent account.')) return;
    try {
      await approveApplication(appId, user.username);
      fetchData();
      alert('Agent activated.');
    } catch (err) {
      alert('Failed: ' + (err.response?.data || err.message));
    }
  };

  const openReject = (app) => {
    setSelectedApp(app);
    setRejectReason('');
    setRejectDialog(true);
  };

  const handleReject = async () => {
    try {
      await rejectApplication({
        applicationId: selectedApp.applicationId,
        reason: rejectReason,
        rejectedBy: user.username,
      });
      setRejectDialog(false);
      fetchData();
    } catch (err) {
      alert('Failed: ' + (err.response?.data || err.message));
    }
  };

  const canReview = user?.roleRights?.includes('ReviewAgentApplication');
  const canApprove = user?.roleRights?.includes('ApproveAgentApplication');

  const pendingReview = useMemo(() => applications.filter(a => a.status === 'PendingReview'), [applications]);
  const pendingApproval = useMemo(() => applications.filter(a => a.status === 'PendingApproval'), [applications]);
  const finalised = useMemo(() => applications.filter(a => a.status === 'Approved' || a.status === 'Rejected'), [applications]);

  const columns = useMemo(() => [
    { id: 'id', header: 'ID', accessorKey: 'applicationId', size: 50 },
    { id: 'name', header: 'Name', accessorFn: row => handleAgentName(row.firstName, row.lastName, row.alias), size: 130 },
    { id: 'ic', header: 'IC', accessorKey: 'icNumber', size: 120 },
    { id: 'phone', header: 'Phone', accessorKey: 'phoneNumber', size: 110 },
    { id: 'email', header: 'Email', accessorFn: row => row.email || '—', size: 140 },
    { id: 'username', header: 'Username', accessorKey: 'username', size: 100 },
    { id: 'inviter', header: 'Invited By', accessorKey: 'inviterName', size: 120 },
    { id: 'group', header: 'Group', accessorFn: row => row.groupName ?? '—', size: 90 },
    { id: 'psc', header: 'PSC', accessorFn: row => row.personalComm != null ? `${row.personalComm}%` : '—', size: 55 },
    { id: 'doc', header: 'DOC', accessorFn: row => row.downlineOverridingComm != null ? `${row.downlineOverridingComm}%` : '—', size: 55 },
    { id: 'toc', header: 'TOC', accessorFn: row => row.teamOverridingComm != null ? `${row.teamOverridingComm}%` : '—', size: 55 },
    {
      id: 'status', header: 'Status', accessorKey: 'status', size: 120,
      Cell: ({ cell }) => {
        const val = cell.getValue();
        const s = STATUS_COLORS[val] ?? { bg: '#F1F5F9', color: '#64748B', label: val };
        return <Chip label={s.label} size="small" sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.65rem', fontFamily: PP }} />;
      },
    },
    {
      id: 'created', header: 'Applied', size: 90,
      Cell: ({ row }) => convertDateTimeToDateString(row.original.createdOn),
    },
    {
      id: 'actions', header: '', size: 240, enableSorting: false, enableColumnFilter: false,
      Cell: ({ row }) => {
        const app = row.original;
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <span onClick={(e) => { e.stopPropagation(); openAgreement(app); }}
              style={{ color: '#64748B', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem', fontFamily: PP, textDecoration: 'underline' }}>View Agreement</span>
            {app.status === 'PendingReview' && canReview && (
              <>
                <span onClick={(e) => { e.stopPropagation(); openReview(app); }}
                  style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem', fontFamily: PP }}>Review &amp; Submit</span>
                <span onClick={(e) => { e.stopPropagation(); openReject(app); }}
                  style={{ color: '#EF4444', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem', fontFamily: PP }}>Reject</span>
              </>
            )}
            {app.status === 'PendingApproval' && canApprove && (
              <>
                <span onClick={(e) => { e.stopPropagation(); handleApprove(app.applicationId); }}
                  style={{ color: '#10B981', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem', fontFamily: PP }}>Approve</span>
                <span onClick={(e) => { e.stopPropagation(); openReject(app); }}
                  style={{ color: '#EF4444', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem', fontFamily: PP }}>Reject</span>
              </>
            )}
            {app.status === 'PendingApproval' && !canApprove && (
              <span style={{ color: '#64748B', fontSize: '0.75rem', fontFamily: PP }}>Awaiting Superadmin…</span>
            )}
            {app.status === 'Approved' && (
              <Chip label="Active" size="small" sx={{ backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 600, fontSize: '0.65rem' }} />
            )}
            {app.status === 'Rejected' && (
              <Chip label="Rejected" size="small" sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 600, fontSize: '0.65rem' }} />
            )}
          </Box>
        );
      },
    },
  ], [canReview, canApprove]);

  const reviewTable = useMaterialReactTable({
    columns, data: pendingReview,
    enableColumnActions: false, enableColumnDragging: false,
    enableDensityToggle: false, enableFullScreenToggle: false, enableHiding: false,
    enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: PP, padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: PP, padding: '6px 10px' } },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        if (e.target.tagName === 'SPAN' && e.target.style.cursor === 'pointer') return;
        if (canReview) openReview(row.original);
      },
      sx: { cursor: canReview ? 'pointer' : 'default', '&:hover': { backgroundColor: '#F0F7FF', transition: 'background-color 0.12s' } },
    }),
    muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
    muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
    muiSearchTextFieldProps: { placeholder: 'Search…', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: PP } } },
    muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50] },
  });

  const approvalTable = useMaterialReactTable({
    columns, data: pendingApproval,
    enableColumnActions: false, enableColumnDragging: false,
    enableDensityToggle: false, enableFullScreenToggle: false, enableHiding: false,
    enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: PP, padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: PP, padding: '6px 10px' } },
    muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
    muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
    muiSearchTextFieldProps: { placeholder: 'Search…', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: PP } } },
    muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50] },
  });

  const historyTable = useMaterialReactTable({
    columns, data: finalised,
    enableColumnActions: false, enableColumnDragging: false,
    enableDensityToggle: false, enableFullScreenToggle: false, enableHiding: false,
    enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: PP, padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: PP, padding: '6px 10px' } },
    muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
    muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
    muiSearchTextFieldProps: { placeholder: 'Search…', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: PP } } },
    muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50] },
  });

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      {!embedded && (
        <Box sx={{ mb: 2 }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>Agent Applications</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: PP }}>Review and approve new agent applications</p>
        </Box>
      )}

      {canReview && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 1 }}>
            Pending Review ({pendingReview.length})
          </Typography>
          <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <MaterialReactTable table={reviewTable} />
          </div>
        </Box>
      )}

      {canApprove && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 1 }}>
            Pending Final Approval ({pendingApproval.length})
          </Typography>
          <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <MaterialReactTable table={approvalTable} />
          </div>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 1 }}>
          History ({finalised.length})
        </Typography>
        <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <MaterialReactTable table={historyTable} />
        </div>
      </Box>

      {/* Review Dialog */}
      <Dialog open={reviewDialog} onClose={() => setReviewDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.1rem' }}>
          Review Application — {handleAgentName(selectedApp?.firstName, selectedApp?.lastName, selectedApp?.alias)}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Alert severity="info" sx={{ borderRadius: '8px', fontFamily: PP }}>
            Set the commission rates, then submit. The applicant's account will be created (still inactive)
            and queued for Superadmin's final approval.
          </Alert>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>
              Upline
            </Typography>
            <TextField size="small" fullWidth value={selectedApp?.inviterName || '—'} disabled
              InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#F1F5F9' } }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>
              Group
            </Typography>
            <TextField size="small" fullWidth value={selectedApp?.inviterGroupName || '—'} disabled
              InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#F1F5F9' } }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>PSC %</Typography>
              <TextField size="small" fullWidth type="number" value={reviewForm.personalComm} onChange={(e) => setReviewForm(prev => ({ ...prev, personalComm: e.target.value }))}
                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#FAFAFA' } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>DOC %</Typography>
              <TextField size="small" fullWidth type="number" value={reviewForm.doc} onChange={(e) => setReviewForm(prev => ({ ...prev, doc: e.target.value }))}
                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#FAFAFA' } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>TOC %</Typography>
              <TextField size="small" fullWidth type="number" value={reviewForm.toc} onChange={(e) => setReviewForm(prev => ({ ...prev, toc: e.target.value }))}
                InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#FAFAFA' } }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReviewDialog(false)} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleReview} sx={{
            background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: PP, fontWeight: 600,
            textTransform: 'none', borderRadius: '8px', boxShadow: 'none',
            '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' },
          }}>Submit for Approval</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.1rem', color: '#991B1B' }}>
          Reject Application
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>Reason for rejection</Typography>
          <TextField size="small" fullWidth multiline minRows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter reason..." InputProps={{ sx: { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px', backgroundColor: '#FAFAFA' } }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectDialog(false)} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Agreement Dialog */}
      <Dialog open={agreementDialog} onClose={() => setAgreementDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.1rem' }}>
          Agreement Preview
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {agreementApp && (
            <AgreementContent data={{
              fullName: handleAgentName(agreementApp.firstName, agreementApp.lastName, agreementApp.alias) || '—',
              icNumber: agreementApp.icNumber,
              address: agreementApp.address,
              groupName: agreementApp.groupName,
              effectiveDate: convertDateTimeToDateString(agreementApp.approvedOn || agreementApp.createdOn),
              personalComm: agreementApp.personalComm,
              downlineOverridingComm: agreementApp.downlineOverridingComm,
              roles: [agreementApp.intendedRole || 'Sales Agent'],
            }} />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAgreementDialog(false)} sx={{ fontFamily: PP, textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
